import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useState, ChangeEventHandler } from "react";
import produce from "immer";
import { Dialog } from "@mui/material";
import { getAuth } from "firebase/auth";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useDialog } from "app/hook";
import { RootState } from "app/store";
import { DialogType } from "app/types";
import firebase from "app/Auth";
import { ParentCloseBtn, FadeTransition } from "common/elements";
import { isIos, useScale, btnDelay } from "common/utils";
import I18n from "features/shared/i18n";
import { deleteAccount } from "features/account/api";
import { ParentConfirmModal } from "features/shared";

enum DeleteStep {
  WARNING = "warning",
  FORM = "form",
}

const getOptions = () => [
  { id: 1, message: I18n.t("MSG_SETTING_PARENTS_USER_UNSUBSCRIBE_OPTION_1") },
  { id: 2, message: I18n.t("MSG_SETTING_PARENTS_USER_UNSUBSCRIBE_OPTION_2") },
  { id: 3, message: I18n.t("MSG_SETTING_PARENTS_USER_UNSUBSCRIBE_OPTION_3") },
  { id: 4, message: I18n.t("MSG_SETTING_PARENTS_USER_UNSUBSCRIBE_OPTION_4") },
  { id: 5, message: I18n.t("MSG_SETTING_PARENTS_USER_UNSUBSCRIBE_OPTION_5") },
  { id: 6, message: I18n.t("MSG_SETTING_PARENTS_USER_UNSUBSCRIBE_OPTION_6") },
];

interface DeleteAccountModalProps {
  open: boolean;
  onClose: () => void;
}

export const DeleteAccountModal = ({ open, onClose }: DeleteAccountModalProps) => {
  const { scale } = useScale();
  const navigate = useNavigate();
  const handleDialog = useDialog();
  const parent = useSelector((state: RootState) => state.user.parent);
  const [step, setStep] = useState(DeleteStep.WARNING);
  const [detail, setDetail] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState<number[]>([]);

  const handleClose = () => {
    btnDelay(() => {
      setStep(DeleteStep.WARNING);
      setSelectedOptions([]);
      setDetail("");
      setConfirmOpen(false);
      onClose();
    });
  };

  const handleNext = () => {
    btnDelay(() => setStep(DeleteStep.FORM));
  };

  const handleSelect =
    (id: number): ChangeEventHandler<HTMLInputElement> =>
    (e) => {
      setSelectedOptions(
        produce((draft) => {
          if (e.target.checked) {
            draft.push(id);
          } else {
            return draft.filter((v) => v !== id);
          }
        }),
      );
    };

  const handleChangeDetail: ChangeEventHandler<HTMLTextAreaElement> = (e) => {
    setDetail(e.target.value);
  };

  const handleOpenConfirm = () => {
    if (selectedOptions.length > 0) {
      btnDelay(() => setConfirmOpen(true));
    }
  };

  const handleCloseConfirm = () => {
    btnDelay(() => setConfirmOpen(false));
  };

  const handleDelete = () => {
    setConfirmOpen(false);
    btnDelay(async () => {
      handleDialog({ type: DialogType.LOADING, value: true });
      try {
        await deleteAccount({
          uid: parent.uid,
          options: getOptions()
            .filter((value) => selectedOptions.includes(value.id))
            .map((v) => v.message),
          detail,
        });
        const parentsAuth = getAuth(firebase);
        await parentsAuth.signOut();
        handleDialog({ type: DialogType.LOADING, value: false });
        navigate("/");
      } catch (error) {
        console.error(error);
        handleDialog({ type: DialogType.LOADING, value: false });
        handleDialog({ type: DialogType.ERROR_NETWORK, value: true });
      }
    });
  };

  return (
    <>
      <ParentConfirmModal
        open={confirmOpen}
        title={I18n.t("MSG_SETTING_PARENTS_USER_UNSUBSCRIBE_TITLE")}
        message={I18n.t("MSG_SETTING_PARENTS_USER_UNSUBSCRIBE_MESSAGE")}
        icon="delete_forever"
        variant="danger"
        confirmLabel={I18n.t("MSG_SETTING_PARENTS_USER_PROFILE_UNSUBSCRIBE_BTN")}
        onClose={handleCloseConfirm}
        onConfirm={handleDelete}
      />

      <Dialog
        fullScreen
        open={open}
        maxWidth={false}
        sx={{ zIndex: 90000 }}
        TransitionComponent={FadeTransition}
        PaperProps={{ style: { backgroundColor: "transparent" } }}
      >
        <div className="flex-col-center w-full h-full bg-textcolor-black/50">
          <div style={{ transform: `scale(${scale})` }}>
            <div className="flex-col-el w-[760px] border-solid border-gray2-30 border-[16px] rounded-[16px] bg-gray2-30">
              <div className="flex-col-el justify-start w-full h-[750px] relative p-6 rounded-[8px] bg-white">
                <ParentCloseBtn close={handleClose} />
                {step === DeleteStep.WARNING ? (
                  <>
                    {/* Step 1: 警告 */}
                    <div className="flex-row-el items-center gap-2 mb-4 pb-3 border-b border-outline shrink-0">
                      <span className="material-symbols-outlined !text-[54px] text-red-100">
                        delete_forever
                      </span>
                      <h2 className="text-[33px] text-textcolor-black">
                        {I18n.t("MSG_SETTING_PARENTS_USER_UNSUBSCRIBE_WARNING_TITLE")}
                      </h2>
                    </div>

                    <div className="flex-col-el gap-4 py-4">
                      <div className="flex-row-center gap-4">
                        <WarningAmberIcon sx={{ fontSize: 40, color: "#FFA03B" }} />
                      </div>

                      <div className="flex-col-el gap-2 mt-4">
                        {I18n.t("MSG_SETTING_PARENTS_USER_UNSUBSCRIBE_WARNING_MESSAGE")
                          .split("\n")
                          .map((line, index) => (
                            <p
                              key={index}
                              className="text-body text-font-size-body2 text-textcolor-black whitespace-pre-wrap"
                            >
                              {line}
                            </p>
                          ))}
                      </div>
                    </div>

                    <div className="flex-row-el flex-center gap-4 mt-auto">
                      <button
                        onClick={handleClose}
                        className="flex-row-el items-center justify-center w-[160px] py-3 bg-white border border-gray2-60 text-textcolor-black rounded-[12px] text-[24px] cursor-pointer hover:bg-gray2-20 transition-all"
                      >
                        {I18n.t("MSG_CANCEL_BTN")}
                      </button>
                      <button
                        onClick={handleNext}
                        className="flex-row-el items-center justify-center w-[160px] py-3 bg-ink text-white border-none rounded-[12px] text-[24px] cursor-pointer hover:opacity-90 transition-all"
                      >
                        <span>{I18n.t("MSG_NEXT_BTN")}</span>
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Step 2: フォーム */}
                    <div className="flex-row-el items-center gap-2 mb-4 pb-3 border-b border-outline shrink-0">
                      <span className="material-symbols-outlined !text-[54px] text-red-100">
                        feedback
                      </span>
                      <h2 className="text-[33px] text-textcolor-black">
                        {I18n.t("MSG_SETTING_PARENTS_USER_UNSUBSCRIBE_FORM_TITLE")}
                      </h2>
                    </div>

                    <div className="flex-col-el gap-3 py-4">
                      {getOptions().map((option) => (
                        <div key={option.id} className="flex-row-el items-center gap-2">
                          <input
                            id={`unsubscribe-form-${option.id}`}
                            type="checkbox"
                            className="w-[24px] h-[24px] cursor-pointer"
                            checked={selectedOptions.includes(option.id)}
                            onChange={handleSelect(option.id)}
                          />
                          <label
                            htmlFor={`unsubscribe-form-${option.id}`}
                            className="text-body text-left text-[24px] text-textcolor-black cursor-pointer"
                          >
                            {option.message}
                          </label>
                        </div>
                      ))}
                    </div>

                    <div className="flex-col-el w-full mt-4">
                      <p className="text-body text-[21px] text-gray2-100 mb-2">
                        {I18n.t("MSG_SETTING_PARENTS_USER_UNSUBSCRIBE_FEEDBACK_LABEL")}
                      </p>
                      <textarea
                        className="resize-none w-full h-[100px] p-3 text-body text-textcolor-black text-[24px] bg-white border-2 border-gray-80/40 rounded-[8px] outline-none focus:border-red-100 transition-colors box-border"
                        rows={4}
                        maxLength={256}
                        value={detail}
                        onChange={handleChangeDetail}
                        autoFocus={!isIos()}
                      />
                    </div>

                    <div className="flex-row-el flex-center gap-4 mt-auto">
                      <button
                        onClick={handleClose}
                        className="flex-row-el items-center justify-center w-[160px] py-3 bg-white border border-gray2-60 text-textcolor-black rounded-[12px] text-[24px] cursor-pointer hover:bg-gray2-20 transition-all"
                      >
                        {I18n.t("MSG_CANCEL_BTN")}
                      </button>
                      <button
                        onClick={handleOpenConfirm}
                        disabled={selectedOptions.length === 0}
                        className="flex-row-el items-center justify-center px-6 py-3 bg-danger text-white border-none rounded-[12px] text-[24px] cursor-pointer hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {I18n.t("MSG_SETTING_PARENTS_USER_PROFILE_UNSUBSCRIBE_BTN")}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </Dialog>
    </>
  );
};
